import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  Paper,
  Rating,
  CircularProgress,
  Button
} from '@mui/material';
import {
  ArrowBack,
  LocationOn
} from '@mui/icons-material';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import UserRatingStats from '../components/UserRatingStats';

interface UserProfile {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  location?: string;
  rating?: number;
  ratingCount?: number;
  photoURL?: string;
  bio?: string;
}

const PublicProfileScreen: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      loadUserProfile(userId);
    } else {
      setError('ID utilisateur manquant');
      setLoading(false);
    }
  }, [userId]);

  const loadUserProfile = async (uid: string) => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfile({
          firstName: userData.firstName,
          lastName: userData.lastName,
          displayName: userData.displayName || userData.profile?.displayName,
          location: userData.location || userData.profile?.location,
          rating: userData.rating || userData.profile?.rating || 0,
          ratingCount: userData.ratingCount || 0,
          photoURL: userData.photoURL || userData.profile?.photoURL,
          bio: userData.bio || userData.profile?.bio
        });
      } else {
        setError('Utilisateur non trouvé');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Profil non trouvé'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Retour
          </Button>
        </Paper>
      </Container>
    );
  }

  const displayName = profile.displayName || 
                     (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : 'Utilisateur');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Bouton retour */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Retour
      </Button>

      {/* Header Profil */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Avatar
              src={profile.photoURL}
              sx={{ width: 150, height: 150, mx: 'auto' }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h4" gutterBottom>
              {displayName}
            </Typography>
            {profile.location && (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                <LocationOn sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {profile.location}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Rating value={profile.rating || 0} readOnly precision={0.5} />
              <Typography variant="body2" color="text.secondary">
                ({profile.ratingCount || 0} avis)
              </Typography>
            </Box>
            {profile.bio && (
              <Typography variant="body1" color="text.secondary">
                {profile.bio}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Notes et avis - VISIBLE PAR TOUT LE MONDE */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        {userId ? (
          <UserRatingStats 
            userId={userId} 
            userType="owner" 
            title="Notes et avis reçus"
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Impossible de charger les notes.
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PublicProfileScreen;

